import React from 'react'
import {
  Alert,
  Box,
  CircularProgress,
  Drawer,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'

import { useSkyReconciliationCommentDay } from '../../../hooks/socialdash/useSkyReconciliation'
import {
  BEING_CHECKED_NEXT_STEP,
  COMMENT_REASON_EXPLANATIONS,
  commentDiffsByReason,
  commentKindLabel,
  commentReasonColor,
  commentReasonLabel,
  fmtInt,
  platformLabel,
  safeHref,
  utcDateTime,
  utcDay,
  utcDayLabel,
  utcTime,
} from '../reconciliationModel'

/**
 * One clicked comment day (design/2026-10-19-sky-reconciliation-on-demand.md,
 * D2): Sprinklr's message list for that platform and UTC day beside ours, and
 * every message on one side only under its reason. Nothing is red: `missed`
 * ("being checked") is amber, with the next step. Each message shows its time
 * and, when Platinum has one, a link to it on its platform; the id is muted. The day is fetched when the drawer opens, never before: it spends
 * Sprinklr calls.
 */
export interface CommentDayDrawerProps {
  /** The day to check; null when closed. X days carry the kind clicked, but the check covers all of X that day. */
  day: { dayMs: number; platform: string; kind: string } | null
  onClose: () => void
}

/** A long reason group is listed up to this many messages. */
const SHOW_AT_MOST = 200

const CommentDayDrawer: React.FC<CommentDayDrawerProps> = ({ day, onClose }) => {
  const open = day !== null
  const date = day ? utcDay(day.dayMs) : ''
  const platform = day?.platform ?? ''
  const { data, isLoading, error } = useSkyReconciliationCommentDay(date, platform, open)
  const groups = commentDiffsByReason(data?.diffs)
  const toExplain = (data?.diffs ?? []).length

  return (
    <Drawer anchor="right" open={open} onClose={onClose} slotProps={{ paper: { sx: { width: { xs: '100%', sm: 560 } } } }}>
      <Box sx={{ p: 2.5 }} data-testid="recon-comment-day">
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
          <Box>
            <Typography variant="h6">
              {platformLabel(platform)}, {day ? utcDayLabel(day.dayMs) : ''} (UTC)
            </Typography>
            {day?.kind && (
              <Typography variant="body2" color="text.secondary">
                Opened from {commentKindLabel(day.kind).toLowerCase()}; every {platformLabel(platform)} message that day is listed.
              </Typography>
            )}
          </Box>
          <IconButton aria-label="Close" onClick={onClose} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {isLoading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">Asking Sprinklr for the day&apos;s messages…</Typography>
          </Box>
        )}
        {error && <Alert severity="error" sx={{ mt: 2 }}>{(error as Error).message}</Alert>}

        {data && (
          <>
            <Typography variant="body1" sx={{ mt: 2, fontVariantNumeric: 'tabular-nums' }} data-testid="recon-comment-day-counts">
              Sprinklr {fmtInt(data.sky)} · Platinum {fmtInt(data.ours)} · {fmtInt(toExplain)} to explain
            </Typography>
            {data.frontierMs > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Platinum&apos;s import complete to {utcDateTime(data.frontierMs)}.
              </Typography>
            )}
            {groups.length === 0 && (
              <Alert severity="success" sx={{ mt: 2 }}>
                Every message matches. The counts may have differed when the page was read; Sprinklr now lists the same messages as Platinum.
              </Alert>
            )}
            {groups.map((g) => {
              const checking = commentReasonColor(g.reason) === 'warning'
              return (
                <Box
                  key={g.reason}
                  sx={{ mt: 2.5, ...(checking ? { pl: 1.5, borderLeft: 3, borderColor: 'warning.main' } : {}) }}
                  data-testid={`recon-day-reason-${g.reason}`}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }} color={checking ? 'warning.dark' : undefined}>
                    {commentReasonLabel(g.reason)} ({fmtInt(g.diffs.length)})
                  </Typography>
                  {COMMENT_REASON_EXPLANATIONS[g.reason] && (
                    <Typography variant="body2" color="text.secondary">{COMMENT_REASON_EXPLANATIONS[g.reason]}</Typography>
                  )}
                  {checking && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{BEING_CHECKED_NEXT_STEP}</Typography>
                  )}
                  <List dense disablePadding>
                    {g.diffs.slice(0, SHOW_AT_MOST).map((d) => {
                      const href = safeHref(d.permalink)
                      const when = d.atMs > 0 ? utcTime(d.atMs) : 'Time not known'
                      return (
                        <ListItem key={d.universalId} disableGutters>
                          <ListItemText
                            primary={
                              <>
                                {when}
                                {href && (
                                  <>
                                    {' · '}
                                    <Link href={href} target="_blank" rel="noopener noreferrer" color="info.main">
                                      Open on {platformLabel(platform)}
                                    </Link>
                                  </>
                                )}
                              </>
                            }
                            secondary={`Sprinklr id ${d.universalId}`}
                            slotProps={{ secondary: { sx: { wordBreak: 'break-all' } } }}
                          />
                        </ListItem>
                      )
                    })}
                  </List>
                  {g.diffs.length > SHOW_AT_MOST && (
                    <Typography variant="caption" color="text.secondary">
                      And {fmtInt(g.diffs.length - SHOW_AT_MOST)} more.
                    </Typography>
                  )}
                </Box>
              )
            })}
          </>
        )}
      </Box>
    </Drawer>
  )
}

export default CommentDayDrawer
